import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import AuditBackLink from "@/components/audit/AuditBackLink";
import ProposalDetail from "@/components/audit/ProposalDetail";
import PageHeader from "@/components/shell/PageHeader";
import EmptyState from "@/components/ui/EmptyState";
import { Page, PageBody } from "@/components/ui/Page";
import { useReloadForNewBuild } from "@/lib/stale-build";
import { trpc } from "@/lib/trpc";
import { useWindowTitleDetail } from "@/lib/window-title";
import { workspaceModuleRedirect } from "@/lib/workspace-module-access";
import { Route as OrgRoute } from "./$orgSlug";

// One Proposal, read-only (chat-first ticket 10, user story 117): where a
// member's "an admin must approve" link lands, so the admin sees exactly what
// is asked before approving it in their own chat. The trailing underscore in
// the file name keeps this page out of the Audit page's layout.
export const Route = createFileRoute("/_authenticated/$orgSlug/audit_/proposals/$proposalId")({
  beforeLoad: async ({ params, context }) => {
    const viewer = await context.queryClient.ensureQueryData(
      trpc.organization.viewer.queryOptions(),
    );
    const target = workspaceModuleRedirect(viewer, params.orgSlug);
    if (target) throw redirect(target);
  },
  loader: ({ params, context }) =>
    context.queryClient.ensureQueryData(
      trpc.audit.proposal.queryOptions({ orgSlug: params.orgSlug, proposalId: params.proposalId }),
    ),
  errorComponent: ProposalLoadError,
  component: ProposalPage,
});

function ProposalLoadError({ error }: { error: unknown }) {
  const { orgSlug } = Route.useParams();
  if (useReloadForNewBuild(error)) return null;
  return (
    <Page>
      <PageHeader title="Proposal" back={<AuditBackLink orgSlug={orgSlug} />} />
      <PageBody>
        <EmptyState title="This Proposal could not be opened.">
          {error instanceof Error ? error.message : String(error)}
        </EmptyState>
      </PageBody>
    </Page>
  );
}

function ProposalPage() {
  const { orgSlug, proposalId } = Route.useParams();
  const { currentOrg } = OrgRoute.useLoaderData();
  const { data: proposal } = useSuspenseQuery(
    trpc.audit.proposal.queryOptions({ orgSlug, proposalId }),
  );
  useWindowTitleDetail(proposal.summary);
  return (
    <Page>
      <PageHeader
        back={<AuditBackLink orgSlug={orgSlug} accountId={proposal.accountId} />}
        title={proposal.summary}
      />
      <PageBody>
        <ProposalDetail orgSlug={orgSlug} companyName={currentOrg.name} proposal={proposal} />
      </PageBody>
    </Page>
  );
}
